
import { supabase } from '@/integrations/supabase/client';

// Keys for fallback local storage - used only if database fetch fails
const PROHIBITED_WORDS_KEY = 'fomo_prohibited_words';
const SOFT_BLOCK_WORDS_KEY = 'fomo_soft_block_words';

// Emoji for soft blocking
const DOLPHIN_EMOJI = '🐬';

// Cache duration for all word lists
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface ModerationWord {
  id: string;
  word: string;
  category: string;
  created_at: string;
}

type ProhibitedWord = ModerationWord;
type SoftBlockWord = ModerationWord;

// In-memory cache for better performance when checking multiple content items
let cachedProhibitedWords: ProhibitedWord[] = [];
let lastCacheTime = 0;

// In-memory cache for soft block words
let cachedSoftBlockWords: SoftBlockWord[] = [];
let lastSoftBlockCacheTime = 0;

/**
 * Fetches the list of soft block words from the database, with localStorage fallback
 */
export const getSoftBlockWords = async (): Promise<SoftBlockWord[]> => {
  const now = Date.now();
  
  // Return cached words if they're still fresh
  if (cachedSoftBlockWords.length > 0 && now - lastSoftBlockCacheTime < CACHE_DURATION) {
    return cachedSoftBlockWords;
  }
  
  try {
    // First try to get from database
    const { data, error } = await supabase
      .from('soft_block_words')
      .select('*');
      
    if (error) {
      throw error;
    }
    
    if (data && data.length > 0) {
      // Update cache
      cachedSoftBlockWords = data;
      lastSoftBlockCacheTime = now;
      
      // Also update localStorage as a fallback
      localStorage.setItem(SOFT_BLOCK_WORDS_KEY, JSON.stringify(data));
      
      return data;
    }
    
    // If no data in database, try localStorage as fallback
    const storedWords = localStorage.getItem(SOFT_BLOCK_WORDS_KEY);
    if (storedWords) {
      const parsedWords = JSON.parse(storedWords);
      return parsedWords as SoftBlockWord[];
    }
    
    // If nothing in localStorage either, return empty array
    return [];
  } catch (error) {
    console.error('Error getting soft block words from database:', error);
    
    // Try localStorage as fallback if database fails
    try {
      const storedWords = localStorage.getItem(SOFT_BLOCK_WORDS_KEY);
      if (storedWords) {
        return JSON.parse(storedWords) as SoftBlockWord[];
      }
    } catch (localError) {
      console.error('Error getting soft block words from localStorage:', localError);
    }
    
    return [];
  }
};

/**
 * Applies soft blocking to text, replacing prohibited words with dolphin emojis
 */
export const applySoftBlocking = async (text: string): Promise<string> => {
  try {
    if (!text) return text;
    
    const softBlockWords = await getSoftBlockWords();
    if (!softBlockWords.length) {
      console.log('[Soft Block] No soft block words loaded');
      return text;
    }
    
    console.log(`[Soft Block] Processing text against ${softBlockWords.length} soft block words`);
    let processedText = text;
    let replacementCount = 0;
    
    // Replace each soft blocked word with a dolphin emoji
    for (const { word } of softBlockWords) {
      const wordLower = word.toLowerCase();
      
      // Escape special regex characters in the word
      const escapedWord = escapeRegex(wordLower);
      
      // Create a regex that matches the word as a whole word, case insensitive
      const regex = new RegExp(`\\b${escapedWord}\\b`, 'gi');
      
      const beforeLength = processedText.length;
      processedText = processedText.replace(regex, DOLPHIN_EMOJI);
      
      if (processedText.length !== beforeLength) {
        replacementCount++;
        console.log(`[Soft Block] Replaced soft-blocked word: "${word}"`);
      }
    }
    
    if (replacementCount > 0) {
      console.log(`[Soft Block] Replaced ${replacementCount} soft-blocked words with ${DOLPHIN_EMOJI}`);
    }
    
    return processedText;
  } catch (error) {
    console.error('Error applying soft blocking:', error);
    return text; // Return original text if there's an error
  }
};

/**
 * Fetches the list of prohibited words from the database, with localStorage fallback
 * Uses caching to minimize database calls
 */
export const getProhibitedWords = async (): Promise<ProhibitedWord[]> => {
  const now = Date.now();
  
  // Return cached words if they're still fresh
  if (cachedProhibitedWords.length > 0 && now - lastCacheTime < CACHE_DURATION) {
    return cachedProhibitedWords;
  }
  
  try {
    // First try to get from database
    const { data, error } = await supabase
      .from('prohibited_words')
      .select('*');
      
    if (error) {
      throw error;
    }
    
    if (data && data.length > 0) {
      // Update cache
      cachedProhibitedWords = data;
      lastCacheTime = now;
      
      // Also update localStorage as a fallback
      localStorage.setItem(PROHIBITED_WORDS_KEY, JSON.stringify(data));
      
      return data;
    }
    
    // If no data in database, try localStorage as fallback
    const storedWords = localStorage.getItem(PROHIBITED_WORDS_KEY);
    if (storedWords) {
      const parsedWords = JSON.parse(storedWords);
      cachedProhibitedWords = parsedWords;
      lastCacheTime = now;
      return parsedWords as ProhibitedWord[];
    }
    
    // If nothing in localStorage either, return empty array
    return [];
  } catch (error) {
    console.error('Error getting prohibited words from database:', error);
    
    // Try localStorage as fallback if database fails
    try {
      const storedWords = localStorage.getItem(PROHIBITED_WORDS_KEY);
      if (storedWords) {
        const parsedWords = JSON.parse(storedWords) as ProhibitedWord[];
        cachedProhibitedWords = parsedWords;
        lastCacheTime = now;
        return parsedWords;
      }
    } catch (localError) {
      console.error('Error getting prohibited words from localStorage:', localError);
    }
    
    return cachedProhibitedWords.length > 0 ? cachedProhibitedWords : [];
  }
};

/**
 * Checks if content contains any prohibited words
 * @param content The text content to check
 * @returns Object with result and first prohibited word found (if any)
 */
export const checkForProhibitedContent = async (content: string): Promise<{ 
  isProhibited: boolean; 
  prohibitedWord?: string;
  category?: string;
}> => {
  if (!content) {
    return { isProhibited: false };
  }
  
  try {
    const prohibitedWords = await getProhibitedWords();
    
    if (!prohibitedWords.length) {
      console.warn('[Content Moderation] ⚠️ WARNING: No prohibited words loaded from database! Content moderation is disabled.');
      return { isProhibited: false };
    }
    
    console.log(`[Content Moderation] Checking content against ${prohibitedWords.length} prohibited words`);
    
    const contentLower = content.toLowerCase();
    
    // Check for whole word matches only
    for (const { word, category } of prohibitedWords) {
      const wordLower = word.toLowerCase();
      
      // Escape special regex characters in the word
      const escapedWord = escapeRegex(wordLower);
      
      // Create a regex that matches the word as a whole word, not as part of another word
      const regex = new RegExp(`\\b${escapedWord}\\b`, 'i');
      
      if (regex.test(contentLower)) {
        console.error(`[Content Moderation] BLOCKED - Found prohibited word: "${word}" (category: ${category})`);
        return { 
          isProhibited: true, 
          prohibitedWord: word,
          category 
        };
      }
    }
    
    console.log('[Content Moderation] Content passed - no prohibited words found');
    return { isProhibited: false };
  } catch (error) {
    console.error('Error checking prohibited content:', error);
    // CRITICAL: If check fails, return false to avoid blocking legitimate content
    return { isProhibited: false };
  }
};

/**
 * Returns a user-friendly message for prohibited content
 */
export const getProhibitedContentMessage = (prohibitedWord?: string, category?: string): string => {
  if (!prohibitedWord) {
    return "Your message contains content that isn't allowed on FOMO. Please revise and try again.";
  }
  
  if (category === 'racist') {
    return `Your message contains language that appears to be racist or discriminatory ("${prohibitedWord}"). FOMO does not tolerate racism, hate speech, or discrimination. Please revise your message.`;
  }
  
  return `Your message contains prohibited content ("${prohibitedWord}"). This type of language is not tolerated on FOMO. Please revise and try again.`;
};
